import { Link } from "@inertiajs/react";
import { CirclePlusIcon, EllipsisIcon, UploadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Invoice, PropsWithUser } from "@/types";

import { PageTitle } from "@/components/page-title";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/utils/format-date";
import InvoiceTypeBadge from "@/components/invoice-type-badge";
import InvoiceStatusBadge from "@/components/invoice-status-badge";
import { formatMoney } from "@/utils/format-money";
import { TableCellLink } from "@/components/table-cell-link";
import { Badge } from "@/components/ui/badge";

type Props = {
  invoices: Invoice[];
  draft_invoices_count: number;
};

function InvoiceIndex({
  invoices,
  draft_invoices_count,
}: PropsWithUser<Props>) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <PageTitle>Invoices</PageTitle>
        <div className="flex items-center gap-4">
          <div>
            <Button asChild variant="secondary">
              <Link href="/draft_invoices">
                Draft invoices
                {draft_invoices_count > 0 && (
                  <Badge variant="outline" className="hover:no-underline">
                    {draft_invoices_count}
                  </Badge>
                )}
              </Link>
            </Button>
          </div>
          <Button asChild>
            <Link href="/draft_invoices/new">
              <UploadIcon />
              Upload invoices
            </Link>
          </Button>
          <Button asChild>
            <Link href="/invoices/new">
              <CirclePlusIcon />
              Add invoice
            </Link>
          </Button>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Issue date</TableHead>
              <TableHead>Number</TableHead>
              <TableHead>Merchant</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Base amount</TableHead>
              <TableHead className="text-right">VAT amount</TableHead>
              <TableHead className="text-right">Total amount</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const href = `/invoices/${invoice.id}`;

              return (
                <TableRow
                  key={invoice.id}
                  className="has-[[data-row-link]:focus-visible]:outline-ring/50 has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2"
                >
                  <TableCell>
                    <TableCellLink href={href} />
                    {formatDate(invoice.issue_date)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    <TableCellLink href={href} tabIndex={-1} />
                    {invoice.number}
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} tabIndex={-1} />
                    {invoice.merchant.name}
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} tabIndex={-1} />
                    <InvoiceTypeBadge type={invoice.type} />
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} tabIndex={-1} />
                    <InvoiceStatusBadge status={invoice.status} />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <TableCellLink href={href} tabIndex={-1} />
                    {formatMoney(invoice.base_amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <TableCellLink href={href} tabIndex={-1} />
                    {formatMoney(invoice.vat_amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    <TableCellLink href={href} tabIndex={-1} />
                    {formatMoney(invoice.total_amount)}
                  </TableCell>
                  <TableCell>
                    <InvoiceActions invoice={invoice} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function InvoiceActions({ invoice }: { invoice: Invoice }) {
  return (
    <AlertDialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <AlertDialogTrigger asChild>
            <DropdownMenuItem>Delete</DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete invoice</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete invoice
            with number <span className="font-semibold">{invoice.number}</span>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <Link
              href={`/invoices/${invoice.id}`}
              method="delete"
              preserveScroll
            >
              Delete
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default InvoiceIndex;
